import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, Copy, Check, CheckSquare, ChevronDown, ChevronUp, Mail, User, Calendar, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentRefresh, QUERY_KEYS } from '../ai/hooks';
import { AGENT_FOCUS_EVENT } from '../ai/context/AIAgentContext';
import type { UIFocus } from '../ai/types/agent';
import { CreateTodoModal } from '../components/todos';
import { Toast } from '../components/ui';
import { createTodo } from '../services/todoService';
import type { CreateTodoInput } from '../types/todo';
import {
  getFormById,
  getFormSubmissions,
  createForm,
  updateForm,
  deleteForm,
  createFormField,
  updateFormField,
  deleteFormField,
  updateFieldOrders,
  generateSlug,
  generateFieldKey,
} from '../services/formService';
import type { FormSubmissionWithAnswers } from '../types/forms';
import { FieldEditor } from '../components/forms/FieldEditor';
import { AddFieldModal } from '../components/forms/AddFieldModal';
import type { FormWithFields, FormFieldType, UpdateFormFieldInput } from '../types/forms';
import { LEAD_TYPE_DEFAULT_LABELS } from '../types/forms';
import { LEAD_STATUS_LABELS } from '../types/crm';
import { logger } from '../lib/logger';

export function FormEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === 'new';

  const [form, setForm] = useState<FormWithFields | null>(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddField, setShowAddField] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);
  const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmissionWithAnswers[]>([]);
  const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(new Set());

  // Refs for field elements to enable scrolling
  const fieldRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [leadTypeDefault, setLeadTypeDefault] = useState<'CUSTOMER' | 'PARTNER' | 'NONE'>('CUSTOMER');
  const [defaultStatus, setDefaultStatus] = useState('NEW');
  const [defaultSource, setDefaultSource] = useState('');

  const loadForm = useCallback(async () => {
    if (!user || !id) return;
    try {
      const data = await getFormById(id, user.id);
      if (data) {
        setForm(data);
        setName(data.name);
        setDescription(data.description || '');
        setSlug(data.slug);
        setIsPublished(data.is_published);
        setLeadTypeDefault(data.lead_type_default);
        setDefaultStatus(data.default_status || 'NEW');
        setDefaultSource(data.default_source || '');

        // Load submissions
        const submissionsData = await getFormSubmissions(id, user.id);
        setSubmissions(submissionsData);
      } else {
        navigate('/forms');
      }
    } catch (error) {
      logger.error('Error loading form:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, id, navigate]);

  useEffect(() => {
    if (!isNew && user && id) {
      loadForm();
    }
  }, [id, user, isNew, loadForm]);

  // Listen for AI Agent form updates
  useAgentRefresh([QUERY_KEYS.FORMS, id ? `forms:detail:${id}` : ''], loadForm);

  // Listen for AI Agent focus events to highlight/scroll to fields
  useEffect(() => {
    const handleFocus = (event: Event) => {
      const customEvent = event as CustomEvent<UIFocus>;
      const focus = customEvent.detail;

      if (focus.type === 'field') {
        setFocusedFieldId(focus.id);

        // Scroll to the field after a short delay
        setTimeout(() => {
          const fieldElement = fieldRefs.current.get(focus.id);
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 150);

        // Clear focus after 3 seconds
        setTimeout(() => {
          setFocusedFieldId(null);
        }, 3000);
      }
    };

    window.addEventListener(AGENT_FOCUS_EVENT, handleFocus);
    return () => {
      window.removeEventListener(AGENT_FOCUS_EVENT, handleFocus);
    };
  }, []);

  const handleNameChange = (newName: string) => {
    setName(newName);
    if (isNew || !form) {
      setSlug(generateSlug(newName));
      setDefaultSource(`Formular: ${newName}`);
    }
  };

  const handleSave = async () => {
    if (!user || !name.trim() || !slug.trim()) return;
    setIsSaving(true);

    try {
      if (isNew) {
        const newForm = await createForm(user.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          slug: slug.trim(),
          is_published: isPublished,
          lead_type_default: leadTypeDefault,
          default_status: defaultStatus || undefined,
          default_source: defaultSource.trim() || undefined,
        });
        navigate(`/forms/${newForm.id}`, { replace: true });
      } else if (form) {
        await updateForm(form.id, user.id, {
          name: name.trim(),
          description: description.trim() || null,
          slug: slug.trim(),
          is_published: isPublished,
          lead_type_default: leadTypeDefault,
          default_status: defaultStatus || null,
          default_source: defaultSource.trim() || null,
        });
        await loadForm();
      }
    } catch (error) {
      logger.error('Error saving form:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user || !form) return;
    if (!confirm('Möchtest du dieses Formular wirklich löschen?')) return;

    try {
      await deleteForm(form.id, user.id);
      navigate('/forms');
    } catch (error) {
      logger.error('Error deleting form:', error);
    }
  };

  const handleAddField = async (type: FormFieldType, label: string) => {
    if (!user || !form) return;

    try {
      const newField = await createFormField(user.id, {
        form_id: form.id,
        type,
        label,
        field_order: form.fields.length,
        field_key: generateFieldKey(label),
        required: false,
      });
      setForm({ ...form, fields: [...form.fields, newField] });
    } catch (error) {
      logger.error('Error adding field:', error);
    }
  };

  const handleUpdateField = async (fieldId: string, data: UpdateFormFieldInput) => {
    if (!user || !form) return;

    try {
      const updatedField = await updateFormField(fieldId, user.id, data);
      setForm({
        ...form,
        fields: form.fields.map((f) => (f.id === fieldId ? updatedField : f)),
      });
    } catch (error) {
      logger.error('Error updating field:', error);
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!user || !form) return;
    if (!confirm('Möchtest du dieses Feld wirklich löschen?')) return;

    try {
      await deleteFormField(fieldId, user.id);
      setForm({
        ...form,
        fields: form.fields.filter((f) => f.id !== fieldId),
      });
    } catch (error) {
      logger.error('Error deleting field:', error);
    }
  };

  const handleMoveField = async (fieldId: string, direction: 'up' | 'down') => {
    if (!user || !form) return;

    const currentIndex = form.fields.findIndex((f) => f.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= form.fields.length) return;

    const newFields = [...form.fields];
    [newFields[currentIndex], newFields[newIndex]] = [newFields[newIndex], newFields[currentIndex]];

    const updates = newFields.map((f, i) => ({ id: f.id, field_order: i }));

    setForm({ ...form, fields: newFields });

    try {
      await updateFieldOrders(user.id, updates);
    } catch (error) {
      logger.error('Error reordering fields:', error);
    }
  };

  const copyPublicUrl = async () => {
    const url = `${window.location.origin}/f/${slug}`;
    await navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  const toggleSubmissionExpanded = (submissionId: string) => {
    setExpandedSubmissions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(submissionId)) {
        newSet.delete(submissionId);
      } else {
        newSet.add(submissionId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSubmissionPreview = (submission: FormSubmissionWithAnswers) => {
    const emailAnswer = submission.answers.find(
      (a) => a.field?.type === 'EMAIL' || a.field?.field_key?.toLowerCase() === 'email'
    );
    const nameAnswer = submission.answers.find(
      (a) => a.field?.field_key?.toLowerCase() === 'name' || a.field?.label?.toLowerCase().includes('name')
    );
    return {
      email: emailAnswer?.value || null,
      name: nameAnswer?.value || null,
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/forms')}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] dark:text-white">
                {isNew ? 'Neues Formular' : 'Formular bearbeiten'}
              </h1>
              {form?.is_system && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  Standard
                </span>
              )}
            </div>
            {!isNew && isPublished && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs sm:text-sm text-gray-500 dark:text-slate-400 truncate">/f/{slug}</span>
                <button
                  onClick={copyPublicUrl}
                  className="p-1 text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                  title="Link kopieren"
                >
                  {copiedLink ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <a
                  href={`/f/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                  title="Formular öffnen"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 self-end sm:self-auto flex-shrink-0">
          {!isNew && form && !form.is_system && (
            <button
              onClick={handleDelete}
              className="p-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-colors font-medium"
              title="Formular löschen"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !slug.trim()}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            <span>{isSaving ? 'Speichern...' : 'Speichern'}</span>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Einstellungen</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="z.B. Kontaktformular"
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Beschreibung
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Kurze Beschreibung des Formulars..."
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                URL-Slug *
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-slate-400">/f/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="kontakt"
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
                Veröffentlicht
              </label>
              <button
                type="button"
                onClick={() => setIsPublished(!isPublished)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  isPublished ? 'bg-primary' : 'bg-gray-200 dark:bg-slate-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    isPublished ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">Lead-Integration</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Lead-Typ bei Einreichung
              </label>
              <select
                value={leadTypeDefault}
                onChange={(e) => setLeadTypeDefault(e.target.value as 'CUSTOMER' | 'PARTNER' | 'NONE')}
                className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
              >
                {Object.entries(LEAD_TYPE_DEFAULT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {leadTypeDefault !== 'NONE' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Standard-Status
                  </label>
                  <select
                    value={defaultStatus}
                    onChange={(e) => setDefaultStatus(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white"
                  >
                    {Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Standard-Quelle
                  </label>
                  <input
                    type="text"
                    value={defaultSource}
                    onChange={(e) => setDefaultSource(e.target.value)}
                    placeholder="z.B. Formular: Kontakt"
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                  />
                </div>
              </>
            )}
          </div>

          {/* Todo Button */}
          {!isNew && form && user && (
            <button
              onClick={() => setIsTodoModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-xl transition-colors font-medium"
            >
              Todo erstellen
              <CheckSquare className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">Formularfelder</h2>
            {!isNew && form && (
              <button
                onClick={() => setShowAddField(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                Feld hinzufügen
              </button>
            )}
          </div>

          {isNew ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-8 text-center">
              <p className="text-gray-500 dark:text-slate-400">
                Speichere das Formular zuerst, um Felder hinzuzufügen.
              </p>
            </div>
          ) : form && form.fields.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-8 text-center">
              <p className="text-gray-500 dark:text-slate-400 mb-4">
                Noch keine Felder vorhanden. Füge dein erstes Feld hinzu.
              </p>
              <button
                onClick={() => setShowAddField(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Erstes Feld hinzufügen
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {form?.fields.map((field, index) => (
                <FieldEditor
                  key={field.id}
                  ref={(el) => {
                    if (el) {
                      fieldRefs.current.set(field.id, el);
                    } else {
                      fieldRefs.current.delete(field.id);
                    }
                  }}
                  field={field}
                  isFirst={index === 0}
                  isLast={index === form.fields.length - 1}
                  isFocused={focusedFieldId === field.id}
                  onUpdate={handleUpdateField}
                  onDelete={handleDeleteField}
                  onMoveUp={(id) => handleMoveField(id, 'up')}
                  onMoveDown={(id) => handleMoveField(id, 'down')}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submissions Section */}
      {!isNew && form && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-gray-900 dark:text-white">Einsendungen</h2>
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-sm font-medium rounded-full">
                  {submissions.length}
                </span>
              </div>
            </div>
          </div>

          {submissions.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-slate-400">
                Noch keine Einsendungen vorhanden.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {submissions.map((submission) => {
                const preview = getSubmissionPreview(submission);
                const isExpanded = expandedSubmissions.has(submission.id);

                return (
                  <div key={submission.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <button
                      onClick={() => toggleSubmissionExpanded(submission.id)}
                      className="w-full p-4 flex items-center gap-4 text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          {preview.name && (
                            <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                              <User className="w-4 h-4 text-gray-400" />
                              {preview.name}
                            </span>
                          )}
                          {preview.email && (
                            <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-slate-400">
                              <Mail className="w-4 h-4" />
                              {preview.email}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(submission.submitted_at)}
                          </span>
                          {submission.lead_id && (
                            <Link
                              to={`/leads/${submission.lead_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline"
                            >
                              → Lead anzeigen
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-gray-400">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4">
                        <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl p-4 space-y-3">
                          {submission.answers
                            .sort((a, b) => (a.field?.field_order || 0) - (b.field?.field_order || 0))
                            .map((answer) => (
                              <div key={answer.id} className="flex flex-col sm:flex-row sm:gap-4">
                                <span className="text-sm font-medium text-gray-500 dark:text-slate-400 sm:w-1/3">
                                  {answer.field?.label || 'Unbekanntes Feld'}
                                </span>
                                <span className="text-sm text-gray-900 dark:text-white sm:w-2/3 whitespace-pre-wrap break-words">
                                  {answer.value || '–'}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AddFieldModal
        isOpen={showAddField}
        onClose={() => setShowAddField(false)}
        onAdd={handleAddField}
      />

      {/* Todo Modal */}
      {!isNew && form && user && (
        <CreateTodoModal
          isOpen={isTodoModalOpen}
          onClose={() => setIsTodoModalOpen(false)}
          onSubmit={handleCreateTodo}
          defaultTitle={`Formular: ${form.name}`}
          defaultDescription="Formular auf Website einbinden"
          tag="form"
          sourceType="form"
          sourceId={form.id}
        />
      )}
    </div>
  );
}
